<template>
  <div
    class="detail-view"
    :class="{ 'detail-view--mobile': isMobile }"
  >
    <!-- Mobile Header -->
    <MobileHeader
      v-if="isMobile"
      :lottery="lottery"
      :countdown="countdown"
      :show-watch-live="isStreamableGame"
      @go-back="goBackToLotteries"
      @watch-live="showStream = true"
    />

    <div class="content-layout">
      <div class="main-content-wrapper">
        <!-- Desktop Header -->
        <DesktopHeader
          v-if="!isMobile && lottery"
          :lottery="lottery"
          :countdown="countdown"
          :show-watch-live="isStreamableGame"
          @go-back="goBackToLotteries"
          @watch-live="showStream = true"
        />

        <!-- Mobile Draws Section -->
        <MobileDrawsSection
          v-if="isMobile"
          :draws="gameStore.getValidDraws"
          :selected-draws="selectedDrawsArray"
          @update-selected-draws="handleUpdateSelectedDraws"
          @toggle-draw-selection="toggleDrawSelection"
        />

        <!-- Desktop Draws Section -->
        <DesktopDrawsSection
          v-else
          :draws="gameStore.getValidDraws"
          :selected-draws="selectedDrawsArray"
          @update-selected-draws="handleUpdateSelectedDraws"
          @toggle-draw-selection="toggleDrawSelection"
        />

        <!-- Related Games Selector -->
        <div
          v-if="lottery && hasRelatedGamesForLottery && !isLite"
          class="related-games-inline-wrapper"
        >
          <div class="related-games-header">
            <span class="related-games-title">{{ $t('detail.alsoAddTo') }}
            </span>
          </div>
          <RelatedGamesSelector
            :current-game-slug="lottery.slug"
            :selected-games="selectedRelatedGames"
            :is-processing="isPlacingBet"
            @update:selected-games="selectedRelatedGames = $event"
          />
        </div>

        <!-- Mobile Betting Section -->
        <MobileBettingSection
          v-if="isMobile"
          :key="`mobile-betting-${isMobile}`"
          :betting-markets="bettingMarkets"
          :selected-market="selectedMarket"
          :hot-numbers="gameStore.getHotNumbers"
          :cold-numbers="gameStore.getColdNumbers"
          :betslip-length="betslip.length"
          :streamlined-selection="streamlinedNumberSelection"
          :enable-hot-cold-numbers="hotColdNumbersEnabled"
          @select-market="selectMarket"
          @remove-market="removeMarket"
          @toggle-group="toggleGroup"
          @tab-changed="onMobileTabChanged"
          @add-line="addLine"
          @remove-line="removeLine"
          @add-to-betslip="(mobileLines) => addToBetslip(mobileLines)"
          @clear-all="clearAllLines"
        />

        <!-- Desktop Betting Section -->
        <DesktopBettingSection
          v-if="!isMobile"
          :key="`desktop-betting-${!isMobile}`"
          :betting-markets="bettingMarkets"
          :selected-market="selectedMarket"
          :hot-numbers="gameStore.getHotNumbers"
          :cold-numbers="gameStore.getColdNumbers"
          :betslip-length="betslip.length"
          :streamlined-selection="streamlinedNumberSelection"
          :enable-hot-cold-numbers="hotColdNumbersEnabled"
          @select-market="selectMarket"
          @remove-market="removeMarket"
          @toggle-group="toggleGroup"
          @tab-changed="onMobileTabChanged"
          @add-line="addLine"
          @remove-line="removeLine"
          @add-to-betslip="(desktopLines) => addToBetslip(desktopLines)"
          @clear-all="clearAllLines"
        />

        <!-- Mobile Confirm Section -->
        <MobileConfirmSection
          v-if="isMobile && selectedMarket && (selectedMarket.select_numbers || isStandardBetPlusColorBonusMarket(selectedMarket.market?.slug ?? '')) && mobileActiveTab === 'main' && !streamlinedNumberSelection"
          :key="`mobile-confirm-${isMobile}`"
          :lines="lines"
          :current-game-slug="lottery?.slug"
          :selected-related-games="selectedRelatedGames"
          :is-processing="isPlacingBet"
          @clear-all="clearAllLines"
          @remove-line="removeLine"
          @add-to-betslip="addToBetslip"
          @update:selected-related-games="selectedRelatedGames = $event"
        />

        <!-- Desktop Confirm Section -->
        <DesktopConfirmSection
          v-if="!isMobile && selectedMarket && (selectedMarket.select_numbers || isStandardBetPlusColorBonusMarket(selectedMarket.market?.slug ?? '')) && !streamlinedNumberSelection"
          :key="`desktop-confirm-${!isMobile}`"
          :lines="lines"
          :current-game-slug="lottery?.slug"
          :selected-related-games="selectedRelatedGames"
          :is-processing="isPlacingBet"
          @clear-all="clearAllLines"
          @remove-line="removeLine"
          @add-to-betslip="addToBetslip"
          @update:selected-related-games="selectedRelatedGames = $event"
        />
        <!-- Mobile Back Button - Always visible on mobile -->
        <div
          v-if="isMobile"
          class="mobile-back-container"
        >
          <button
            class="mobile-back-button"
            @click="goBackToLotteries"
          >
            <svg
              class="mobile-back-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span class="mobile-back-text">{{ $t('detail.backToLotteries') }}</span>
          </button>
        </div>
      </div>
      <div
        v-if="useOnlinePlatformBetslip"
        class="betslip-wrapper"
      >
        <BetSlip
          @remove-bet="removeBetFromSlip"
          @clear="clearBetslip"
          @clearedAfterSubmission="handleClearedAfterSubmission"
          @selectDraws="handleSelectDraws"
          @drawsChanged="handleDrawsChangedFromBetSlip"
        />
      </div>
    </div>

    <!-- Pinned footer: stays in the iframe/mobile viewport (not at the bottom of the full document). -->
    <Teleport to="body">
      <div
        v-if="isMobile && useOnlinePlatformBetslip"
        class="mobile-view-bets-container"
      >
        <button
          type="button"
          class="mobile-view-bets"
          @click="openBetslip"
        >
          {{ $t('detail.viewBets') }} ({{ betslip.length }})
        </button>
      </div>
    </Teleport>

    <VideoLiveStream
      v-if="showStream && lottery"
      :game-slug="lottery.slug"
      @close="showStream = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, defineEmits, defineExpose, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBetslipStore } from "../stores/betslip";
import { useGameStore } from "../stores/game";
import { useAuthStore } from "../stores/auth";
import { useToast } from "../composables/useToast";
import { useModalStore } from "../stores/modal";
import type { BetslipItem } from "../stores/betslip";
import BetSlip from "../components/BetSlip.vue";
import MobileDrawsSection from "../components/mobile/MobileDrawsSection.vue";
import DesktopDrawsSection from "../components/desktop/DesktopDrawsSection.vue";
import MobileBettingSection from "../components/mobile/MobileBettingSection.vue";
import MobileConfirmSection from "../components/mobile/MobileConfirmSection.vue";
import MobileHeader from "../components/mobile/MobileHeader.vue";
import DesktopHeader from "../components/desktop/DesktopHeader.vue";
import DesktopBettingSection from "../components/desktop/DesktopBettingSection.vue";
import DesktopConfirmSection from "../components/desktop/DesktopConfirmSection.vue";
import RelatedGamesSelector from "../components/RelatedGamesSelector.vue";
import VideoLiveStream from "../components/VideoLiveStream.vue";
import { canShowLiveStream } from "../stores/stream";
import { useRelatedGames } from "../composables/useRelatedGames";
import { useRelatedGameMatching } from "../composables/useRelatedGameMatching";
import { iframeCommunication, DETAIL_MOBILE_VIEWPORT_CLASS } from "../utils/iframeCommunication";
import { resolveMarketOutcome, normalizeSpecifier } from "@/constants/sameColorBalls";
import { isStandardBetPlusColorBonusMarket } from "@/constants/standardBetPlusColorBonus";
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import { buildSelectedDrawsArray, parseDrawQuery } from '@/utils/deepLink';

const route = useRoute();
const router = useRouter();
const gameStore = useGameStore();
const authStore = useAuthStore();
const { showToast } = useToast();
const { t } = useI18n();
const modalStore = useModalStore();
const { isLite } = useIntegrationMode();
const emit = defineEmits(['openBetslipModal', 'drawsChanged']);

const useOnlinePlatformBetslip = computed(() => {
  const ch = gameStore.getBetSettings?.client_surface_ui?.online;
  return ch?.use_platform_betslip !== false;
});

const onlineUiSettings = computed(() => gameStore.getBetSettings?.client_surface_ui?.online);
const streamlinedNumberSelection = computed(
  () => onlineUiSettings.value?.streamlined_number_selection === true
);
const hotColdNumbersEnabled = computed(
  () => !isLite.value && onlineUiSettings.value?.enable_hot_cold_numbers === true
);

interface Game {
  id: string;
  name: string;
  slug: string;
  flagUrl: string;
  drawTime: Date;
  cutoffTime: Date;
  country: string;
  draw_date?: string;
  cutoff?: string;
  game_name?: string;
  game_slug?: string;
  game_logo?: string;
}

interface MarketOutcome {
  id: number;
  game_market_id?: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface Line {
  numbers: number[];
  bonus?: number;
  bonusColor?: import('@/constants/sameColorBalls').BallColorSlug;
  numBets: number;
  picks: number;
  market?: SelectedMarket;
}

const lottery = ref<Game | null>(null);
const showStream = ref(false);
const selectedDrawsArray = ref<boolean[]>([]);

const applyDrawSelectionFromQuery = () => {
  const nextDraws = gameStore.getValidDraws || [];
  const drawId = parseDrawQuery(route.query.draw);
  selectedDrawsArray.value = buildSelectedDrawsArray(nextDraws, drawId);
};
const selectedMarket = ref<SelectedMarket | null>(null);
const mobileActiveTab = ref<'main' | 'bonus'>('main');
const isMobile = ref(window.innerWidth <= 768);
const lines = ref<Line[]>([]);
const bettingMarkets = ref<MarketGroup[]>([]);
const DETAIL_STICKY_HEADER_CLASS = 'detail-sticky-header';

const syncMobileViewportLayout = () => {
  const html = document.documentElement;
  // Full-page scroll like list views — do not lock a middle scrollport.
  document.body.classList.remove(DETAIL_MOBILE_VIEWPORT_CLASS);
  html.style.removeProperty('--detail-mobile-viewport-height');

  // Allow sticky mobile header (overflow-x:hidden on #app otherwise blocks it).
  document.body.classList.toggle(DETAIL_STICKY_HEADER_CLASS, isMobile.value);

  if (isMobile.value && useOnlinePlatformBetslip.value) {
    html.style.setProperty('--detail-mobile-fixed-footer-height', '72px');
  } else {
    html.style.removeProperty('--detail-mobile-fixed-footer-height');
  }

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendImmediateHeightUpdate();
  }
};

const syncBettingMarketsFromStore = () => {
  const marketGroups = gameStore.getMarketGroups || [];
  bettingMarkets.value = marketGroups.map(group => ({
    id: group.id,
    name: group.name,
    slug: group.slug,
    ordering: group.ordering,
    has_bonus: group.has_bonus,
    rules: group.rules,
    markets: group.markets.map(market => ({
      id: market.id,
      name: market.name,
      slug: market.slug,
      description: market.description,
      ordering: market.ordering,
      outcomes: market.outcomes || []
    }))
  }));
};

const isClearingAfterSubmission = ref(false);
const lastAddedBet = ref<BetslipItem | null>(null);
const selectedRelatedGames = ref<string[]>([]);
const { getRelatedGamesSync } = useRelatedGames();
const { findMatchingBet, createRelatedBet } = useRelatedGameMatching();

const hasRelatedGamesForLottery = computed(() => {
  if (!lottery.value) return false;
  const related = getRelatedGamesSync(lottery.value.slug);
  return Array.isArray(related) && related.length > 0;
});

const isStreamableGame = computed(() =>
  lottery.value?.slug
    ? canShowLiveStream(lottery.value.slug, gameStore.getBetSettings?.enabled_modules)
    : false
);

const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

const updateCountdown = () => {
  const validDraws = gameStore.getValidDraws;
  if (!validDraws || validDraws.length === 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  const earliestDraw = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    const earliestTime = new Date(earliest.cutoff || earliest.draw_date);
    return cutoffTime < earliestTime ? draw : earliest;
  }, validDraws[0]);
  
  try {
    const now = new Date();
    const cutoffRaw = earliestDraw.cutoff || earliestDraw.draw_date;
    
    let cutoffTime: Date;
    if (typeof cutoffRaw === 'string') {
      const dateStr = cutoffRaw.includes('T') ? cutoffRaw : cutoffRaw.replace(' ', 'T');
      cutoffTime = new Date(dateStr);
    } else {
      cutoffTime = new Date(cutoffRaw);
    }
    
    if (isNaN(cutoffTime.getTime())) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }
    
    const diff = cutoffTime.getTime() - now.getTime();

    if (diff <= 0) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    countdown.value = {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  } catch (error) {
    console.error('Error updating countdown:', error);
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
  }
};

let countdownTimer: number;

const betslipStore = useBetslipStore();
const { addBet, removeBet: removeBetFromSlip, clearBetslip } = betslipStore;
const betslip = computed(() => betslipStore.betslip);

watch(
  () => route.params.slug,
  async (newSlug) => {
    if (newSlug) {
      try {
        await gameStore.fetchGameDetails(newSlug as string)
        const gameDetail = gameStore.getGameDetail;
        if (gameDetail) {
          lottery.value = {
            id: gameDetail.id.toString(),
            name: gameDetail.name,
            slug: gameDetail.slug,
            flagUrl: gameDetail.logo,
            drawTime: new Date(gameDetail.current_draw.draw_date),
            cutoffTime: new Date(gameDetail.current_draw.cutoff),
            country: gameDetail.game_type.name,
            draw_date: gameDetail.current_draw.draw_date,
            cutoff: gameDetail.current_draw.cutoff,
            game_name: gameDetail.name,
            game_slug: gameDetail.slug,
            game_logo: gameDetail.logo
          };

          applyDrawSelectionFromQuery();

          syncBettingMarketsFromStore();
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
        router.push({ path: '/all', query: { ...route.query } });
      }
    }
  },
  { immediate: true }
);

watch(
  () => route.query,
  async () => {
    if (route.params.slug) {
      await gameStore.fetchGameDetails(route.params.slug as string);
      syncBettingMarketsFromStore();
    }
  },
  { deep: true }
);

watch(
  () => gameStore.gameDetails,
  () => {
    if (gameStore.gameDetails) {
      syncBettingMarketsFromStore();
    }
  }
);

watch(
  () => authStore.account || sessionStorage.getItem('auth_account'),
  async (account, previousAccount) => {
    if (!account || account === previousAccount || !route.params.slug) {
      return;
    }

    try {
      await gameStore.fetchGameDetails(route.params.slug as string, false);
      syncBettingMarketsFromStore();
    } catch (error) {
      console.error('Failed to refresh game details for account:', error);
    }
  }
);

watch(
  () => selectedDrawsArray.value,
  (newSelection) => {
    const selectedDraws = gameStore.getValidDraws.filter((_, index) => newSelection[index]);
    emit('drawsChanged', selectedDraws);
  },
  { deep: true }
);

watch(
  () => lottery.value,
  (newLottery) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newLottery?.cutoff) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => gameStore.getValidDraws,
  (newDraws) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }

    if (route.query.draw) {
      applyDrawSelectionFromQuery();
    }
    
    if (newDraws && newDraws.length > 0) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => route.query.draw,
  () => {
    applyDrawSelectionFromQuery();
  }
);


const openBetslip = () => {
  if (!useOnlinePlatformBetslip.value) {
    showToast({
      title: t('toast.externalBetslip'),
      message: t('toast.externalBetslipMsg'),
      type: 'info',
      duration: 4000,
    });
    return;
  }
  modalStore.openBetslip();
};

const goBackToLotteries = () => {
  router.push({
    name: 'all',
    query: { ...route.query },
  });
};

const clearAllLines = () => {
  lines.value = [];
  selectedRelatedGames.value = [];
};

const handleClearedAfterSubmission = () => {
  isClearingAfterSubmission.value = true;
  lines.value = [];
  mobileActiveTab.value = 'main';
  
  applyDrawSelectionFromQuery();
};

const selectDraws = (drawCount: number) => {
  const availableDraws = gameStore.getValidDraws || [];
  const maxDraws = Math.min(availableDraws.length, drawCount);
  
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  for (let i = 0; i < maxDraws; i++) {
    selectedDrawsArray.value[i] = true;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleSelectDraws = (_drawData: {
  betIndex: number;
  drawCount: number;
  selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>;
  gameId: string | number;
}) => {
  // Disabled: draw selection UI should be independent from betslip edits
  return;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleDrawsChangedFromBetSlip = (_selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>) => {
  // Only reset to first draw if betslip is empty
  if (betslip.value.length === 0 && !isClearingAfterSubmission.value) {
    const availableDraws = gameStore.getValidDraws || [];
    if (availableDraws.length > 0 && !selectedDrawsArray.value.some(Boolean)) {
      selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
      selectedDrawsArray.value[0] = true;
    }
  }
};

const toggleDrawSelection = (index: number) => {
  selectedDrawsArray.value[index] = !selectedDrawsArray.value[index];
};

const handleUpdateSelectedDraws = (index: number, checked: boolean) => {
  selectedDrawsArray.value[index] = checked;
};

const expandedGroupIndex = ref<number | null>(0);
const toggleGroup = (idx: number) => {
  expandedGroupIndex.value = expandedGroupIndex.value === idx ? null : idx;
};

const onMobileTabChanged = (tab: 'main' | 'bonus') => {
  mobileActiveTab.value = tab;
};

const selectMarket = async (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}) => {
  if (!bet) return;
  
  if (bet.select_numbers) {
    selectedMarket.value = bet;
    if (isMobile.value) {
      mobileActiveTab.value = 'main';
      onMobileTabChanged('main');
    }
  } else {
    selectedMarket.value = null;
    const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
    
    const outcome = resolveMarketOutcome(
      bet.market.outcomes,
      bet.outcome_id,
      bet.specifier,
      bet.game_market_id
    );

    const specifier =
      bet.specifier ??
      (outcome?.specifier != null ? normalizeSpecifier(outcome.specifier) : null) ??
      null;

    let betType = bet.label;
    let marketDescription = bet.market.description;
    if (outcome?.specifier && bet.label.includes('{value}')) {
      betType = bet.label.replace('{value}', outcome.specifier);
      marketDescription = bet.market.description.replace('{value}', outcome.specifier);
    }
    
    const betslipItem: BetslipItem = {
      market_id: bet.market.id,
      market_group_id: bet.marketGroup.id,
      game_id: gameStore.getGameDetail?.id || 0,
      game_name: gameStore.getGameDetail?.name,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      numbers: [],
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      betType: betType,
      maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
      odds: bet.odds,
      numBets: 1,
      picks: bet.balls,
      outcome_id: bet.outcome_id,
      label: bet.label,
      market_name: bet.market.name,
      market_description: marketDescription,
      specifier: specifier || null,
      game_market_id: bet.game_market_id ?? outcome?.game_market_id ?? undefined,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    if (!useOnlinePlatformBetslip.value) {
      iframeCommunication.sendMessage('bets-added', { bets: [betslipItem] });
      try {
        window.dispatchEvent(
          new CustomEvent('numbers-online-betslip-selection', { detail: { bets: [betslipItem] } })
        );
      } catch {
        /* ignore */
      }
      showToast({
        title: t('toast.selectionSent'),
        message: t('toast.externalIntegrationMsg'),
        type: 'info',
        duration: 4000,
      });
      selectedRelatedGames.value = [];
      modalStore.closeBetslip();
      return;
    }

    let duplicateBets = 0;

    // Twin/sum markets: one outcome per market (Odd replaces Even, not both in slip).
    const twinSiblingIndex = betslip.value.findIndex(
      (item) =>
        item.game_id === betslipItem.game_id &&
        item.market_id === betslipItem.market_id &&
        item.numbers.length === 0 &&
        String(item.outcome_id) !== String(betslipItem.outcome_id)
    );
    if (twinSiblingIndex !== -1) {
      removeBetFromSlip(twinSiblingIndex);
    }

    const betAdded = addBet(betslipItem);
    
    if (!betAdded) {
      duplicateBets++;
      showToast({
        title: t('toast.betAlreadyAdded'),
        message: t('toast.betAlreadyAddedMsg'),
        type: 'info',
        duration: 3000
      });
    } else {
      lastAddedBet.value = betslipItem;
      const allAddedBets: BetslipItem[] = [betslipItem];

      if (selectedRelatedGames.value.length > 0 && lottery.value && hasRelatedGamesForLottery.value) {
        const originalGameSlug = lottery.value.slug;
        const relatedGames = getRelatedGamesSync(lottery.value.slug);
        const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));

        for (const relatedGame of selectedGames) {
          try {
            const matchingResult = await findMatchingBet(relatedGame, betslipItem);
            const relatedBet = createRelatedBet(matchingResult, betslipItem);
            const added = addBet(relatedBet);
            if (!added) {
              duplicateBets++;
            } else {
              allAddedBets.push(relatedBet);
            }
          } catch (error) {
            console.error(`Error adding bet to ${relatedGame.name}:`, error);
          }
        }
        
        // Restore original game
        await gameStore.fetchGameDetails(originalGameSlug, false);
      }

      // Send all added bets upstream
      iframeCommunication.sendMessage('bets-added', { 
        bets: allAddedBets 
      });

      if (duplicateBets > 0) {
        showToast({
          title: t('toast.duplicateBets'),
          message: t('toast.duplicateBetsSkipped', { count: duplicateBets }),
          type: 'info',
          duration: 3000
        });
      }

      selectedRelatedGames.value = [];

      modalStore.closeBetslip();
    }
  }
};

const removeMarket = (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  const betToRemove = betslip.value.find(item => 
    item.outcome_id === bet.outcome_id &&
    item.market_id === bet.market.id
  );
  
  if (betToRemove) {
    removeBetFromSlip(betslip.value.indexOf(betToRemove));
    showToast({
      title: t('toast.betRemoved'),
      message: t('toast.betRemovedMsg', { label: bet.label }),
      type: 'info',
      duration: 2000
    });
  }
};

const addLine = (line: Line) => {
  if (!line || !selectedMarket.value) {
    console.error("No market selected when adding line or line is invalid");
    return;
  }
  
  const lineWithMarket = {
    ...line,
    market: selectedMarket.value
  };
  lines.value.push(lineWithMarket);
};

const removeLine = (index: number) => {
  lines.value.splice(index, 1);
};

const isPlacingBet = ref(false);

const addToBetslip = async (mobileLines?: Line[]) => {
  if (isPlacingBet.value) return;
  
  const linesToProcess = mobileLines || lines.value;
  
  if (linesToProcess.length === 0) {
    console.error("No lines to add");
    return;
  }

  const hasEmbeddedMarket = linesToProcess.some((line) => line.market);
  if (selectedMarket.value === null && !hasEmbeddedMarket) {
    console.error("No market or lines selected");
    return;
  }

  if (!selectedDrawsArray.value.some((selected) => selected)) {
    if (gameStore.getValidDraws.length > 0) {
      selectedDrawsArray.value[0] = true;
    } else {
      console.error("No draws available");
      return;
    }
  }

  const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
  if (!selectedDrawDates.length) {
    console.error("No draw date selected");
    return;
  }

  let duplicateBets = 0;
  const successfullyAddedBets: BetslipItem[] = [];
  let maxBetsReached = false;
  
  for (const line of linesToProcess) {
    if (maxBetsReached) {
      break;
    }

    const lineMarket = line.market || selectedMarket.value;
    if (!lineMarket) {
      console.error("No market information found for line");
      continue;
    }
    
    const isBonusOnly = lineMarket.marketGroup.slug === 'bonus-ball-only-additional-drum' || 
                       lineMarket.marketGroup.slug === 'bonus-ball-only-same-drum';
    const isMainIncludingBonus = lineMarket.marketGroup.slug === 'standard-bet-bonus-from-same-drum';
    const isColorCombo = isStandardBetPlusColorBonusMarket(lineMarket.market?.slug ?? '');
    const numbersPayload = isColorCombo
      ? [Array.isArray(line.numbers) ? line.numbers : [], []]
      : [
          isBonusOnly ? [] : (Array.isArray(line.numbers) ? line.numbers : []),
          (!isMainIncludingBonus && line.bonus !== undefined && line.bonus !== null) ? [line.bonus] : []
        ];
    const bet = {
      market_id: lineMarket.market.id,
      market_group_id: lineMarket.marketGroup.id,
      game_name: gameStore.getGameDetail?.name,
      game_id: gameStore.getGameDetail?.id || 0,
      numbers: numbersPayload,
      bonus: line.bonus,
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      betType: lineMarket.label,
      maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
        odds: lineMarket.odds,
        picks: isBonusOnly ? 1 : (line.picks || lineMarket.balls),
        numBets: 1,
        label: lineMarket.label,
        market_name: lineMarket.market.name,
        market_description: lineMarket.market.description,
        outcome_id: lineMarket.outcome_id,
        game_market_id: lineMarket.game_market_id ?? undefined,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    const betAdded = useOnlinePlatformBetslip.value ? addBet(bet) : true;
    if (!betAdded) {
      duplicateBets++;
    } else {
      successfullyAddedBets.push(bet);
      lastAddedBet.value = bet;
    }
  }

  // Process related games
  if (selectedRelatedGames.value.length > 0 && successfullyAddedBets.length > 0 && lottery.value) {
    const originalGameSlug = lottery.value.slug;
    const relatedGames = getRelatedGamesSync(lottery.value.slug);
    const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));
    const relatedBetsToAdd: BetslipItem[] = [];

    for (const addedBet of successfullyAddedBets) {
      for (const relatedGame of selectedGames) {
        try {
            const matchingResult = await findMatchingBet(relatedGame, addedBet);
            const relatedBet = createRelatedBet(matchingResult, addedBet);
            const added = useOnlinePlatformBetslip.value ? addBet(relatedBet) : true;
            if (!added) {
              duplicateBets++;
            } else {
              relatedBetsToAdd.push(relatedBet);
            }
        } catch (error) {
          console.error(`Error adding bet to ${relatedGame.name}:`, error);
        }
      }
    }
    successfullyAddedBets.push(...relatedBetsToAdd);
    
    // Restore original game
    await gameStore.fetchGameDetails(originalGameSlug, false);
  }

  // Send data upstream for all successfully added bets (including related games)
  if (successfullyAddedBets.length > 0) {
    iframeCommunication.sendMessage('bets-added', { 
      bets: successfullyAddedBets 
    });
    if (!useOnlinePlatformBetslip.value) {
      try {
        window.dispatchEvent(
          new CustomEvent('numbers-online-betslip-selection', { detail: { bets: successfullyAddedBets } })
        );
      } catch {
        /* ignore */
      }
      showToast({
        title: t('toast.selectionSent'),
        message: t('toast.externalIntegrationMsg'),
        type: 'info',
        duration: 4000,
      });
    }
  }
  
  if (duplicateBets > 0) {
    showToast({
      title: t('toast.duplicateBets'),
      message: t('toast.duplicateBetsSkipped', { count: duplicateBets }),
      type: 'info',
      duration: 3000
    });
  }

  lines.value = [];
  selectedRelatedGames.value = [];
  mobileActiveTab.value = 'main';
  
};


let drawsRefreshTimer: number | null = null;

const setupDrawRefreshTimer = () => {
  const validDraws = gameStore.getValidDraws;
  if (validDraws.length === 0) return;

  const earliestCutoff = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    return cutoffTime < earliest ? cutoffTime : earliest;
  }, new Date(validDraws[0].cutoff || validDraws[0].draw_date));

  const now = new Date();
  const timeUntilExpiry = earliestCutoff.getTime() - now.getTime();

  if (timeUntilExpiry > 0) {
    if (drawsRefreshTimer) {
      clearTimeout(drawsRefreshTimer);
    }

    drawsRefreshTimer = window.setTimeout(() => {
      gameStore.refreshDraws().then(() => {
        if (countdownTimer) {
          clearInterval(countdownTimer);
        }
        updateCountdown();
        countdownTimer = window.setInterval(updateCountdown, 1000);
        setupDrawRefreshTimer();
      });
    }, timeUntilExpiry + 1000);
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  syncMobileViewportLayout();
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  setupDrawRefreshTimer();
});

watch([isMobile, useOnlinePlatformBetslip], syncMobileViewportLayout);

watch(() => route.path, () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
});

watch(() => gameStore.getValidDraws, (newDraws, oldDraws) => {
  if (newDraws.length !== oldDraws?.length) {
    const newSelection = new Array(newDraws.length).fill(false);
    
    if (oldDraws && oldDraws.length > 0) {
      oldDraws.forEach((oldDraw: { event_id: string | number }, oldIndex: number) => {
        if (selectedDrawsArray.value[oldIndex]) {
          const matchingIndex = newDraws.findIndex((newDraw: { event_id: string | number }) => 
            newDraw.event_id === oldDraw.event_id
          );
          if (matchingIndex !== -1) {
            newSelection[matchingIndex] = true;
          }
        }
      });
    }
    
    if (!newSelection.some(Boolean) && newDraws.length > 0) {
      newSelection[0] = true;
    }
    
    selectedDrawsArray.value = newSelection;
    setupDrawRefreshTimer();
  }
}, { immediate: true });

watch(() => betslip.value.length, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    if (!isClearingAfterSubmission.value) {
      selectedMarket.value = null;
      mobileActiveTab.value = 'main';
    }
    isClearingAfterSubmission.value = false;
  }
});

watch(() => modalStore.showBetslip, (isOpen) => {
  if (!isOpen && isMobile.value) {
    mobileActiveTab.value = 'main';
    lines.value = [];
  }
});

let resizeTimeout: number | null = null;
const handleResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    const newIsMobile = window.innerWidth <= 768;
    if (newIsMobile !== isMobile.value) {
      selectedMarket.value = null;
      lines.value = [];
      mobileActiveTab.value = 'main';
    }
    isMobile.value = newIsMobile;
    syncMobileViewportLayout();
  }, 100);
};

onUnmounted(() => {
  document.body.classList.remove(DETAIL_MOBILE_VIEWPORT_CLASS);
  document.body.classList.remove(DETAIL_STICKY_HEADER_CLASS);
  document.documentElement.style.removeProperty('--detail-mobile-viewport-height');
  document.documentElement.style.removeProperty('--detail-mobile-fixed-footer-height');
  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendImmediateHeightUpdate();
  }
  window.removeEventListener("resize", handleResize);
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  if (drawsRefreshTimer) {
    clearTimeout(drawsRefreshTimer);
  }
});


defineExpose({
  selectDraws
});
</script>

<style scoped>
/* Custom Header Styles */

.view-bets-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  width: 100%;
  padding: 16px;
  background: var(--surface-detail-header);
  border-top: 1px solid var(--tab-button-inactive);
}

.view-bets {
  width: 100%;
  padding: 16px;
  background: var(--colour-red-text-colour);
  color: var(--text-button);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
}

.view-bets:hover {
  background: var(--bg-danger-hover);
}

.detail-view {
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: var(--detail-page-padding, 5px);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.add-to-betslip.processing {
  position: relative;
  pointer-events: none;
  background: var(--surface-detail-header);
}

.add-to-betslip.processing::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid var(--text-button);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
/* Base Layout Styles */
.content-layout {
  display: flex;
  gap: var(--detail-layout-gap, 20px);
  padding: var(--detail-layout-padding, 20px 0);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.main-content-wrapper {
  flex: 1;
  max-width: 70%;
  min-width: 0;
  box-sizing: border-box;
}

.betslip-wrapper {
  width: 30%;
  min-width: 300px;
  max-width: 400px;
  flex-shrink: 0;
  box-sizing: border-box;
}

/* Custom styles specific to DetailView */
.lottery-card-wrapper {
  flex: 1;
  max-width: 80%;
}

.lottery-card-wrapper :deep(.lottery-card) {
  height: 100%;
  margin: 0;
}

.back-button {
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  padding: 32px;
  margin-left: 24px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 600px) {
  .back-button {
    width: 100%;
    order: -1;
    margin-bottom: 12px;
    border-radius: 8px;
    background: var(--surface-detail-header);
    box-shadow: 0 2px 8px var(--shadow-light);
  }
  .lottery-card-wrapper {
    width: 100%;
  }
}

/* Desktop Draws Section - styles live in DesktopDrawsSection.vue */

.betting-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .betting-section {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
}

.number-selection-container {
  padding: 0;
  position: relative;
}

.selection-header {
  margin-bottom: 24px;
}

.add-to-betslip {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--colour-green-button);
  color: var(--text-button);
  border: none;
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
  z-index: 100;
}

.add-to-betslip:hover {
  background: var(--bg-success-hover);
}

.add-to-betslip:disabled {
  background: var(--surface-detail-header);
  cursor: not-allowed;
  opacity: 0.5;
}

.back-button {
  background: transparent;
  border: 2px solid var(--colour-red-text-colour);
  color: var(--colour-base-text-colour);
  cursor: pointer;
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: color 0.2s;
}

.back-button:hover {
  color: var(--colour-red-text-colour);
}

.betting-group {
  background: var(--surface-detail-header);
  border-radius: 12px;
  padding: 12px 16px;
}

.betting-title {
  color: var(--text-detail-header);
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.subtitle {
  color: var(--text-detail-header-muted);
  font-size: 14px;
  margin-bottom: 24px;
}

.betting-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.sum-options {
  grid-template-columns: repeat(2, 1fr);
}

.bet-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-hover);
  padding: 12px 16px;
  border-radius: 8px;
}

.bet-label {
  color: var(--text-detail-row);
  font-size: 14px;
}

.bet-amount {
  color: var(--text-detail-row);
  font-weight: 500;
  font-size: 14px;
}
/* Medium screens (tablets) - Keep grid approach */
@media (max-width: 1024px) and (min-width: 769px) {
  .content-layout {
    display: flex;
    gap: 16px;
    padding: 16px 0px;
  }
  
  .main-content-wrapper {
    flex: 1;
    max-width: 60%;
  }
  
  .betslip-wrapper {
    width: 40%;
    min-width: 300px;
    max-width: 400px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
  
  .draws-grid::before {
    left: calc(33.333% + 5px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 5px);
  }
  
  
  .betting-options {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .sum-options {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .number-selection-container {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 16px;
  }
  
  .selection-header {
    margin-bottom: 16px;
  }
  
  .selection-header .back-button {
    padding: 12px 24px;
    font-size: 14px;
    margin-left: 0;
  }
}

/* Smaller medium screens - Adjust grid proportions */
@media (max-width: 900px) and (min-width: 769px) {
  .main-content-wrapper {
    max-width: 55%;
  }
  
  .betslip-wrapper {
    width: 45%;
    min-width: 280px;
    max-width: 350px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
  
  .draws-grid::before {
    left: calc(33.333% + 4px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 4px);
  }
  
}

/* Large tablets and small desktops */
@media (max-width: 1200px) and (min-width: 1025px) {
  /* .content-layout {
    gap: 20px;
  }
   */
  .main-content-wrapper {
    max-width: 65%;
  }
  
  .betslip-wrapper {
    width: 35%;
    min-width: 280px;
    max-width: 320px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .draws-grid::before {
    left: calc(33.333% + 5px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 5px);
  }
  
}

@media (max-width: 768px) {
  .detail-view {
    padding: 0;
    display: block;
    width: 100%;
    max-width: 100%;
    /* Keep overflow visible so sticky mobile header can pin to the page. */
    overflow: visible;
    height: auto;
    min-height: 0;
  }

  .detail-view--mobile :deep(.mobile-header) {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 120;
    margin-bottom: 8px;
    background: var(--surface-detail-game-header);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
    border-bottom: 1px solid var(--detail-mobile-header-divider, var(--colour-surface-cont, rgba(255, 255, 255, 0.18)));
  }

  .betslip-wrapper {
    display: none !important;
  }
  
  .content-layout {
    display: block;
    margin-top: 0;
    padding: 0;
    width: 100%;
    max-width: 100%;
    overflow: visible;
    height: auto;
  }
  
  .main-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--detail-mobile-section-gap, 10px);
    width: 100%;
    max-width: 100%;
    padding: 0 0 calc(var(--detail-mobile-fixed-footer-height, 72px) + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
    overflow-x: clip;
    overflow-y: visible;
    height: auto;
  }
  
  .draws-section {
    margin-bottom: 24px;
  }
  
  .draws-container {
    margin-top: 12px;
  }
  
  .draws-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;
  }
  
  .draws-grid::before,
  .draws-grid::after {
    display: none;
  }
  
  .draw-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: var(--colour-surface-cont);
    border-radius: 6px;
    border: 1px solid var(--colour-surface-cont);
    cursor: pointer;
  }
  
  .draw-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .time-icon img {
    width: 16px;
    height: 16px;
    filter: brightness(0) saturate(100%) invert(20%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
  }
  
  .draw-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .date {
    font-size: 12px;
    color: var(--colour-secondary-text-colour);
    font-weight: 400;
  }
  
  .time {
    font-size: 12px;
    color: var(--colour-orange-text-colour);
    font-weight: 600;
  }
  
  .checkbox-wrapper {
    width: 16px;
    height: 16px;
  }
  
  .checkmark {
    width: 16px;
    height: 16px;
    border: 2px solid var(--colour-surface-cont-high);
    border-radius: 3px;
  }
  
  .checkbox-wrapper input:checked ~ .checkmark {
    background: var(--colour-surface-cont-high);
    border-color: var(--colour-surface-cont-high);
  }
  
  .checkbox-wrapper .checkmark:after {
    left: 4px;
    top: 1px;
    width: 3px;
    height: 6px;
    border: solid white;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
  
  .betting-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
  }
  
  .betting-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .number-selection-container {
    width: 100%;
    max-width: 100%;
    margin: 16px 0 0 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  .selection-header {
    font-size: 16px;
    padding: 0 0 12px 0;
  }
  
  .number-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
  }
  
  .number-grid > * {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
  }
  
  .quick-hot-cold-row {
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
  }
  
  .add-line-btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .main-content-wrapper {
    padding: 0 0 calc(var(--detail-mobile-fixed-footer-height, 80px) + env(safe-area-inset-bottom, 0px));
  }
  
  .draws-header {
    padding: 0 6px;
  }
  
  .betting-section {
    gap: 8px;
  }
  
  .number-selection-container {
    margin: 12px 0 0 0;
  }
}

.draws-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 0 10px;
}


  .collapse-btn {
    background: none;
    border: none;
    color: var(--text-detail-header);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

/* Mobile View Bets Button (teleported to body — keep button styles scoped) */

.mobile-view-bets {
  width: 100%;
  margin-top: 0;
  padding: 12px;
  background: var(--detail-mobile-action-bg, var(--colour-red-text-colour));
  color: var(--text-button);
  border: none;
  border-radius: var(--detail-mobile-action-radius, 8px);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-transform: none;
  transition: background-color 0.2s;
  display: block;
  box-sizing: border-box;
}

.mobile-view-bets:hover {
  background: var(--bg-danger-hover);
}

/* Mobile Back Button */
.mobile-back-container {
  margin-top: 12px;
}

.mobile-back-button {
  width: 100%;
  padding: var(--detail-mobile-back-padding, 16px);
  background: var(--detail-mobile-back-bg, var(--surface-detail-header));
  color: var(--text-detail-header);
  border: 1px solid var(--detail-mobile-back-border-color, var(--colour-surface-cont));
  border-radius: var(--detail-mobile-back-radius, 8px);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.mobile-back-button:hover {
  background: var(--bg-hover);
  border-color: var(--colour-orange-button);
}

.mobile-back-icon {
  width: 20px;
  height: 20px;
}

.mobile-back-text {
  font-size: 14px;
  font-weight: 600;
}

.related-games-inline-wrapper {
  width: 100%;
  margin: 10px 0;
  padding: var(--detail-panel-padding, 16px);
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--detail-panel-radius, 4px);
  border: var(--detail-panel-border-width, 1px) solid var(--colour-surface-cont);
  box-sizing: border-box;
}

.related-games-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.related-games-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--colour-secondary-text-colour);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
</style>
